const mettascopeReplayUrlPrefix =
  "https://softmax-universe.github.io/softmax/mettascope/mettascope.html?replay=";

const bitworldAmongThemViewerUrl =
  "https://softmax-public.s3.us-east-1.amazonaws.com/bitworld/among_them/1/replay_viewer.html";

const softmaxPublicS3GlobalUrl = "https://softmax-public.s3.amazonaws.com/";
const softmaxPublicS3RegionalUrl =
  "https://softmax-public.s3.us-east-1.amazonaws.com/";
const softmaxPublicS3Uri = "s3://softmax-public/";
const bitworldAmongThemReplayPath = "bitworld/among_them/replays/";
const bitworldAmongThemRegionalReplayUrlPrefix = `${softmaxPublicS3RegionalUrl}${bitworldAmongThemReplayPath}`;
const genericPublicReplayPattern =
  /^(?:s3:\/\/softmax-public\/|https:\/\/softmax-public\.s3(?:\.us-east-1)?\.amazonaws\.com\/)replays\/([^/?#]+)\.json\.z$/;
const publicCoworldJsonReplayPattern =
  /^(?:s3:\/\/softmax-public\/|https:\/\/softmax-public\.s3(?:\.us-east-1)?\.amazonaws\.com\/)replays\/[^/?#]+\.json\.z$/;

export function normalizeReplayUrl(
  replayUrl: string | null | undefined,
): string | null {
  if (!replayUrl) return null;

  for (const viewerPrefix of [
    mettascopeReplayUrlPrefix,
    // Stored viewer links still use the previous organization and repository.
    "https://metta-ai.github.io/metta/mettascope/mettascope.html?replay=",
  ]) {
    if (replayUrl.startsWith(viewerPrefix)) {
      return `${mettascopeReplayUrlPrefix}${replayUrl.slice(viewerPrefix.length)}`;
    }
  }

  return `${mettascopeReplayUrlPrefix}${replayUrl}`;
}

export function bitWorldReplayViewerUrl(
  replayUrl: string | null | undefined,
): string | null {
  if (!replayUrl) return null;

  if (replayUrl.startsWith(bitworldAmongThemViewerUrl)) {
    return replayUrl;
  }

  const regionalReplayUrl = bitWorldReplayUrl(replayUrl);
  return `${bitworldAmongThemViewerUrl}?replay=${encodeURIComponent(regionalReplayUrl)}`;
}

export function bitWorldReplayUrl(replayUrl: string): string {
  const genericReplayMatch = replayUrl.match(genericPublicReplayPattern);

  if (genericReplayMatch) {
    return `${bitworldAmongThemRegionalReplayUrlPrefix}${genericReplayMatch[1]}.rep`;
  }

  return regionalSoftmaxPublicUrl(replayUrl);
}

export function isPublishedBitWorldReplayUrl(replayUrl: string): boolean {
  const regionalReplayUrl = regionalSoftmaxPublicUrl(replayUrl);
  return (
    regionalReplayUrl.startsWith(bitworldAmongThemRegionalReplayUrlPrefix) &&
    regionalReplayUrl.endsWith(".rep")
  );
}

export function publishedCoworldJsonReplayUrl(
  replayUrl: string,
): string | null {
  if (!publicCoworldJsonReplayPattern.test(replayUrl)) return null;

  return regionalSoftmaxPublicUrl(replayUrl);
}

// COWORLDS WHOSE PUBLISHED IMAGE ALSO SHIPS THE WALLED-PIT "LEAGUE REPLAYER"
// ADD-ON — `CoworldReplayFrame`'s `viewerClient="league"`, served at
// `/client/league` beside the plain broadcast board every coworld gets at
// `/client/replay` (see that component's comment on the rewrite). Keyed by
// the coworld's CANONICAL NAME — `league.game.coworld_name` / the denormalized
// `episode.coworld_name` every caller of `coworldReplayRoute` already carries
// as `telemetryContext.game` — not by coworld id, which is per-image and
// churns on every republish.
//
// "ctf" is the coworld's PRE-PIVOT name. "paintbot" is the live one: the game
// was renamed/repointed to Paintbot without this set following it, so every
// surface that gated the walled-pit viewer on it — LeagueDetail's Featured
// Match Theater button, this file's callers — was matching a name that no
// live league reports any more. Verified against production 2026-08-30
// (`GET /api/observatory/v2/leagues`, 126 leagues): zero report
// `coworld_name: "ctf"`; three (Paintbot, Ctf, Elite Paintbot — one shared
// Game record) report `"paintbot"`. With the set stuck on the old name, every
// Paintbot replay on every surface fell back to the plain board, whose known
// bug is a roster overlay that repeats seat names with "(2)".."(8)" suffixes
// over the map — the walled-pit viewer's own per-side roster does not have
// it. "ctf" stays for any already-recorded episode whose denormalized name
// was never backfilled to the new one.
const walledPitTheaterCoworlds = new Set<string>(["ctf", "paintbot"]);

export function supportsWalledPitTheater(
  coworldName: string | null | undefined,
): boolean {
  return coworldName != null && walledPitTheaterCoworlds.has(coworldName);
}

// COWORLDS WHOSE `/watch` THEATER GIVES THE REPLAY THE FULL STAGE WIDTH — the
// left episodes rail folds into the foot of the standings column instead of
// standing beside the picture (see `wideStage` in WatchTheater.tsx). Paintbot
// asked for this (owner, 2026-09-20): `/watch`'s stage mounts the PLAIN
// broadcast board (`/client/replay` — see `viewerClient` on
// `CoworldReplayFrame`; this page never upgrades to the walled-pit league
// shell), and that board already lays itself out with a feed rail against
// its OWN left edge and the map filling the rest, so handing the freed left
// column's width to the iframe puts the feeds back on screen without this
// page drawing a second rail of its own.
// Keyed by `league.game.coworld_name`, exactly like `WALLED_PIT_THEATER_COWORLDS`
// above — but this set is NOT the same membership: it is scoped to the coworld
// the owner actually asked for, not to every coworld the walled-pit viewer
// happens to serve.
const wideStageTheaterCoworlds = new Set<string>(["paintbot"]);

export function supportsWideStageTheater(
  coworldName: string | null | undefined,
): boolean {
  return coworldName != null && wideStageTheaterCoworlds.has(coworldName);
}

function regionalSoftmaxPublicUrl(url: string): string {
  return url
    .replace(softmaxPublicS3Uri, softmaxPublicS3RegionalUrl)
    .replace(softmaxPublicS3GlobalUrl, softmaxPublicS3RegionalUrl);
}
